import React, { useState, useMemo, useEffect } from "react";
import { useInfiniteQuery } from "react-query";
import { statTypes } from "../shared/components/Table/utils";
import { fetchClient } from "../shared/api/fetching";
import PlayersMatchLogTable from "./components/PlayersMatchLogTable";
import { apiURL, mlsPlayerMatchLogStatsAPIv2 } from "../shared/api/variables";
import Dropdown from "../shared/components/Dropdown";
import ButtonGroup from "../shared/components/ButtonGroup";
import ToggleButton from "../shared/components/ToggleButton";
import { formatMatchStatsData } from "./utils";
import { useReactAppState } from "../shared/containers/utils";
import { NoResults } from "../shared/svg/Svg";

function PlayersMatchLog({ options }) {
  const { t, mediaQueries } = useReactAppState();
  const initialStatType =
    options?.position === "goalkeeper"
      ? statTypes.playersMatchLogGoalKeeping
      : statTypes.playersMatchLogGeneral;
  const competitionsDropdownData = [
    { sportecId: "all", name: t("match_all_competitions") },
    ...options?.competitions?.items?.map((item) => {
      return { sportecId: item?.fields?.sportecId, name: item?.title };
    }),
  ];
  const [page, setPage] = useState(0);
  const [competition, setCompetition] = useState(null);
  const [statType, setStatType] = useState(initialStatType);
  const { isLargeMin } = mediaQueries || {};
  const endpointKey = useMemo(() => {
    return `currentQueryType:page:${
      page || page === 0 ? page : null
    }-statType:${
      statType ? statType : initialStatType
    }:-competition:${competition}`;
  }, [statType, competition]);

  useEffect(() => {
    setPage(0);
  }, [statType]);

  useEffect(() => {
    setCompetition("all");
  }, []);

  const mlsPlayerMatchLogStatsAPIv2Endpoint = (page_token) => {
    return mlsPlayerMatchLogStatsAPIv2({
      playerId: options?.player?.fields?.sportecId,
      page_token,
      competitionId: competition,
    });
  };

  const {
    data,
    isLoading: statsLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [`mls-player-match-log-${endpointKey}`],
    queryFn: ({ pageParam }) => {
      return fetchClient({
        endpoint: mlsPlayerMatchLogStatsAPIv2Endpoint(pageParam),
        apiURL: apiURL.statsAPI,
      });
    },
    keepPreviousData: true,
    getNextPageParam: (lastPage) => lastPage.next_page_token,
  });

  const setNextPage = (nextPage) => {
    if (nextPage > page) {
      fetchNextPage();
    } else {
      fetchPreviousPage();
    }
    setPage(nextPage);
  };

  const formatStatsData =
    data && data?.pages[page]?.match_log
      ? formatMatchStatsData(
          data?.pages[page]?.match_log,
          options?.competitions
        )
      : [];

  return (
    <div role="region" className="mls-c-players-match-log">
      {options?.title ? (
        <>
          <div className="mls-o-block-header">
            <div className="mls-o-block-header__title">{t(options?.title)}</div>
          </div>
          <div className="mls-o-block-header mls-o-block-header--sm">
            <div className="mls-o-block-header__title">{t(options?.title)}</div>
          </div>
        </>
      ) : null}
      <div className="mls-c-stats__header">
        {competitionsDropdownData?.length && competition ? (
          <Dropdown
            onChangeFn={(comp) => setCompetition(comp)}
            displayKey={"name"}
            valueKey={"sportecId"}
            items={competitionsDropdownData}
            currentValue={competition}
          />
        ) : null}
        {isLargeMin ? (
          <ButtonGroup
            className={
              "mls-o-buttons__segmented mls-o-buttons__segmented--four desktop"
            }
            value={statType}
            onChange={(e) => setStatType(e.target.value)}
            aria-label="statistics category"
          >
            <ToggleButton
              className="mls-o-buttons__segment"
              value={initialStatType}
              aria-label="general"
            >
              {t("stats_general")}
            </ToggleButton>
            <ToggleButton
              className="mls-o-buttons__segment"
              value={statTypes.playersMatchLogPassing}
              aria-label="passing"
            >
              {t("stats_passing")}
            </ToggleButton>

            <ToggleButton
              className="mls-o-buttons__segment"
              value={statTypes.playersMatchLogAttacking}
              aria-label="attacking"
            >
              {t("stats_attacking")}
            </ToggleButton>

            <ToggleButton
              className="mls-o-buttons__segment"
              value={statTypes.playersMatchLogDefending}
              aria-label="defending"
            >
              {t("stats_defending")}
            </ToggleButton>
          </ButtonGroup>
        ) : (
          <Dropdown
            extraClass={"mobile"}
            onChangeFn={(val) => setStatType(val)}
            displayKey={"name"}
            valueKey={"statType"}
            items={[
              {
                statType: initialStatType,
                name: t("stats_general"),
              },
              {
                statType: statTypes.playersMatchLogPassing,
                name: t("stats_passing"),
              },
              {
                statType: statTypes.playersMatchLogAttacking,
                name: t("stats_attacking"),
              },
              {
                statType: statTypes.playersMatchLogDefending,
                name: t("stats_defending"),
              },
            ]}
            currentValue={statType}
          />
        )}
      </div>
      {statsLoading || formatStatsData?.length ? (
        <PlayersMatchLogTable
          baseClass={"mls-c-players-match-log"}
          playerData={formatStatsData}
          page={page}
          statType={statType}
          setPage={setNextPage}
          hasNextPage={hasNextPage}
          emptyResultsComponent={<></>}
        />
      ) : (
        <div className="mls-c-stats__no-results">
          <NoResults size="large" />
          <div className="mls-c-stats__no-results-text">
            {t("no_match_log_available")}
          </div>
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line no-func-assign
PlayersMatchLog = React.memo(PlayersMatchLog);

export default PlayersMatchLog;
